import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { fadeUp, stagger } from "./motion";
import { ShieldCheck, Leaf } from "lucide-react";
import { useLang } from "../i18n";

const STATS = [
  { value: "40+", id: "countries-served" },
  { value: "2007", id: "family-owned-since" },
  { value: "22+", id: "species-exported" },
  { value: "6", id: "continents-reached" },
];

const REACH_IMG =
  "https://images.pexels.com/photos/16520011/pexels-photo-16520011.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

const CERTS = [
  { icon: Leaf, name: "FSC®" },
  { icon: ShieldCheck, name: "PEFC" },
  { icon: ShieldCheck, name: "NHLA" },
];

export const GlobalReach = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="reach"
      ref={ref}
      data-testid="global-reach"
      className="relative overflow-hidden bg-brand-blue py-28 text-brand-bone md:py-40"
    >
      <motion.img
        style={{ y }}
        src={REACH_IMG}
        alt="Cargo port at dusk"
        className="absolute inset-0 h-[120%] w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-brand-blue/60" />

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.span
            variants={fadeUp}
            className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber"
          >
            {t("reach.kicker")}
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-4 max-w-3xl font-serif text-5xl font-medium leading-[0.95] tracking-tight md:text-7xl"
          >
            {t("reach.title")}
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-20 grid grid-cols-2 gap-x-8 gap-y-12 border-t border-brand-bone/20 pt-14 md:grid-cols-4"
        >
          {STATS.map((s, i) => (
            <motion.div key={s.id} variants={fadeUp} data-testid={`stat-${s.id}`}>
              <div className="font-serif text-6xl font-medium leading-none text-brand-bone md:text-7xl">
                {s.value}
              </div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/60">
                {t("reach.stats")[i]}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-24 grid grid-cols-1 gap-6 md:grid-cols-3"
        >
          {CERTS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.name}
                variants={fadeUp}
                data-testid={`cert-${c.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                className="rounded-sm border border-brand-bone/20 bg-brand-bone/[0.04] p-8 backdrop-blur-sm transition-colors duration-300 hover:border-brand-timber"
              >
                <Icon size={28} className="text-brand-timber" strokeWidth={1.5} />
                <h4 className="mt-6 font-serif text-3xl font-medium">{c.name}</h4>
                <p className="mt-2 text-sm font-light leading-relaxed text-brand-bone/70">
                  {t("reach.certs")[i]}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
